import React, { useRef, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { PageHeader } from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Spinner } from "@/components/Loading";
import { Sparkles, Send, Bot, User } from "lucide-react";
import { toast } from "sonner";

const SUGGESTIONS = [
  "من هم الطلاب الأقل من 75% حضورًا؟",
  "كم عدد الطلاب الذين اجتازوا كل برنامج؟",
  "أعطني ملخصًا عن البرامج النشطة.",
  "ما البرنامج الأعلى نسبة إكمال؟",
];

export default function Assistant() {
  const [messages, setMessages] = useState([
    { role: "assistant", text: "مرحبًا! أنا مساعدك الذكي. اسألني عن البرامج والحضور والطلاب والدرجات." },
  ]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const endRef = useRef(null);

  const send = async (q) => {
    const question = (q ?? input).trim();
    if (!question || loading) return;
    setInput("");
    setMessages((m) => [...m, { role: "user", text: question }]);
    setLoading(true);
    try {
      const { data } = await api.post("/assistant", { question });
      setMessages((m) => [...m, { role: "assistant", text: data.answer }]);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
      setMessages((m) => [...m, { role: "assistant", text: "تعذّر الحصول على رد حاليًا، حاول مرة أخرى." }]);
    } finally {
      setLoading(false);
      setTimeout(() => endRef.current?.scrollIntoView({ behavior: "smooth" }), 100);
    }
  };

  return (
    <>
      <PageHeader title="المساعد الذكي" subtitle="اسأل عن بيانات البرامج والطلاب بلغة طبيعية" />
      <div className="mx-auto flex h-[70vh] max-w-3xl flex-col rounded-2xl border border-slate-100 bg-card soft-shadow">
        <div className="flex-1 space-y-4 overflow-y-auto p-6">
          {messages.map((m, i) => (
            <div key={i} data-testid={`msg-${m.role}`} className={`flex gap-3 ${m.role === "user" ? "flex-row-reverse" : ""}`}>
              <div className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-xl ${m.role === "user" ? "bg-navy text-gold" : "bg-emerald-50 text-greenx"}`}>
                {m.role === "user" ? <User className="h-5 w-5" /> : <Bot className="h-5 w-5" />}
              </div>
              <div className={`max-w-[80%] whitespace-pre-wrap rounded-2xl px-4 py-3 text-sm leading-relaxed ${m.role === "user" ? "bg-navy text-white" : "bg-slate-50 text-slate-700"}`}>
                {m.text}
              </div>
            </div>
          ))}
          {loading && (
            <div className="flex gap-3">
              <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-emerald-50 text-greenx"><Bot className="h-5 w-5" /></div>
              <div className="rounded-2xl bg-slate-50 px-4 py-3"><Spinner className="h-5 w-5" /></div>
            </div>
          )}
          <div ref={endRef} />
        </div>

        {messages.length <= 1 && (
          <div className="flex flex-wrap gap-2 border-t border-slate-100 p-4">
            {SUGGESTIONS.map((s, i) => (
              <button key={i} data-testid={`suggestion-${i}`} onClick={() => send(s)} className="rounded-full border border-slate-200 bg-white px-3 py-1.5 text-xs text-slate-600 transition-colors hover:bg-slate-50">
                <Sparkles className="me-1 inline h-3 w-3 text-gold" /> {s}
              </button>
            ))}
          </div>
        )}

        <div className="flex gap-2 border-t border-slate-100 p-4">
          <Input data-testid="assistant-input" value={input} onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && send()} placeholder="اكتب سؤالك هنا..." className="rounded-xl" />
          <Button data-testid="assistant-send" onClick={() => send()} disabled={loading || !input.trim()} className="rounded-xl bg-navy hover:bg-navy-light"><Send className="h-4 w-4" /></Button>
        </div>
      </div>
    </>
  );
}
